import React, { useState, useEffect, useRef, useContext } from "react";
import { useClient } from "../context/ClientProvider";
import useProduct from "../hooks/useProduct";
import { useInputBlurSave } from '../hooks/useInputBlurSave';
import Questions from '../components/Questions';
import { useLoading } from '../components/LoadingComponent';
import ApiConstants from '../utils/ApiConstants';
import { scrollToTop, scrollToError, scrollToElement, isDebugMode, formatValidationErrors } from '../utils/utils';
import Modal from '../components/Modal';
import useModal from '../hooks/useModal';
import AISSummary from "../components/AISSummary";
import FamilyMatrix from "../components/FamilyMatrix";
import MainPremium from "../components/MainPremium";
import ResultsOffer from "../components/ResultsOffer";
import TotalPremium from '../components/TotalPremium';
import PaymentPaySafe from "../components/PaymentPaySafe";
import PaymentEFT from "../components/PaymentEFT";
import Address from "../components/Address";
import Height from "../components/Height";
import Weight from "../components/Weight";
import ProgressBar from '../components/ProgressBar';
import SaleFlowDesign from '../components/SaleFlowDesign';
import AdvisorsIdentification from "../components/AdvisorsIdentification";
import PolicyInfo from "../components/PolicyInfo";
import SecondAdvisorsIdentification from "../components/SecondAdvisorsIdentification";
import { useAuthenticatedApiFetch } from '../hooks/useAuthenticatedApiFetch';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from '../authConfig';
import { useAppNavigation } from "../hooks/useAppNavigation";
import { LanguageContext } from "../context/LanguageProvider";
import { useProductLanguage } from "../context/ProductLanguageContext";
import { AnimatePresence, motion } from "framer-motion";
import { useSimulation } from "../context/SimulationContext";

const Product = () => {

  const { instance, accounts } = useMsal();
  const authenticatedApiFetch = useAuthenticatedApiFetch(instance);
  const isAuthenticated = useIsAuthenticated();
  const { goToError } = useAppNavigation();
  const { isSimulation, setSimulation, isSimulationLoading, setSimulationLoading } = useSimulation();

  const [paysafeFailed, setPaysafeFailed] = useState(false);

  useEffect(() => {
    // Check if authentication status is fully loaded
    if (isAuthenticated === false && accounts.length === 0) {
      instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, accounts, instance]);

  const client = useClient();

  const { t, language, fetchLanguageForClient } = useContext(LanguageContext);

  const { setProductLanguage } = useProductLanguage(); //  global product language setter

  useEffect(() => {
    setProductLanguage(language);
  }, [language, setProductLanguage]);


  const defaultTitle = "Product";

  const [firstLevelArea, setFirstLevelArea] = useState();
  const [secondLevelArea, setSecondLevelArea] = useState();
  const [subArea, setSubArea] = useState();

  const [previousFirstLevelArea, setPreviousFirstLevelArea] = useState();
  const [previousSecondLevelArea, setPreviousSecondLevelArea] = useState();
  const [previousSubArea, setPreviousSubArea] = useState();

  const [areaNames, setAreaNames] = useState([]);
  const [subAreaName, setSubAreaName] = useState('');

  const [childAreaNames, setChildAreaNames] = useState([]);

  const [questions, setQuestions] = useState([]);
  const formRef = useRef(null);
  const inputRefs = useRef({});

  const { addRequest, removeRequest } = useLoading();

  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const [wasValidated, setWasValidated] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);


  const product = useProduct();

  const [hasNext, setHasNext] = useState(true);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [goToResults, setGoToResults] = useState(false)
  const [isDataSaved, setIsDataSaved] = useState(true);

  const [defaultProductPremium, setDefaultProductPremium] = useState(0);

  const [pctGroup1, setPctGroup1] = useState(0);
  const [pctGroup2, setPctGroup2] = useState(0);

  const [version, setVersion] = useState(null);

  const { goToApplications, } = useAppNavigation();

  //Modal
  const { isModalVisible, modalContent, openModal, closeModal } = useModal();

  const currentAreaName = Array.isArray(areaNames) ? areaNames.find((areaData) => areaData.productAreaId === firstLevelArea)?.name : undefined;

  const [navigateToIdMatrix, setNavigateToIdMatrix] = useState(null);

  useEffect(() => {
    document.title = (currentAreaName ? currentAreaName : "") + (subAreaName ? " / " + subAreaName : "") || defaultTitle;
  }, [currentAreaName, subAreaName]);


  // Fetch initial values for first composition . area, subarea : 0, 0
  useEffect(() => {
    const fetchInitialValues = async () => {
      try {
        addRequest();

        const url = `${ApiConstants.BASE_URL}${ApiConstants.getInitialProductAreasEndpoint(product, client)}`;
        const result = await authenticatedApiFetch(url);

        if (!result) return;

        const { data } = result;

        // If any of the critical values are missing -> go to error page
        if (
          data.firstLevelAreaId == null ||
          data.productSubAreaId == null
        ) {
          console.error('Missing required area IDs:', data);
          goToError();
          return;
        }

        setFirstLevelArea(data.firstLevelAreaId);
        setSecondLevelArea(data.secondLevelAreaId);
        setSubArea(data.productSubAreaId);
        setHasNext(data.hasNext);
        setHasPrevious(data.hasPrevious);
        setGoToResults(data.goToResults);

        if (data.navigateToId) {
          scrollToElement('go_to_' + data.navigateToId);
          setNavigateToIdMatrix(data?.navigateToId || null);
        }

        // If goToResults flag is true → fetch results area
        if (data.goToResults === true) {
          try {
            const resultsUrl = `${ApiConstants.BASE_URL}${ApiConstants.getResultsArea(product, client)}`;

            const resultsResponse = await authenticatedApiFetch(resultsUrl);
            if (
              !resultsResponse ||
              resultsResponse.status !== 200 ||
              !resultsResponse.data
            ) {
              goToError();
              return;
            }

            const resultsData = resultsResponse.data;

            setPreviousFirstLevelArea(resultsData.firstLevelAreaId);

          } catch (error) {
            console.error("Error fetching results area:", error);
            goToError();
          }
        }

        removeRequest();
      } catch (error) {
        goToError();
      } finally {
        // clear ..
      }
    };

    fetchInitialValues();
  }, []);

  useEffect(() => {
    const checkSimulation = async () => {
      const endpoint = ApiConstants.checkIfApplicationIsASimulation(product, client);

      try {
        const result = await authenticatedApiFetch(
          `${ApiConstants.BASE_URL}${endpoint}`,
          {},
          'json',
          language,
          instance
        );

        const isSimulationActive = result?.status === 200 && result.data;
        setSimulation(!!isSimulationActive);
      } catch (error) {
        console.error('Error checking simulation status:', error);
        setSimulation(false);
      }
      finally {
        setSimulationLoading(false); 
      }
    };

    checkSimulation();
  }, [product, client]);

  // fetchAreaNames fetches area
  const fetchAreaNames = async () => {
    try {  
      const endpoint = ApiConstants.getProductAreasForClientEndpoint(product, client);
      const url = `${ApiConstants.BASE_URL}${endpoint}`;
      const result = await authenticatedApiFetch(url);
      if (!result) return;
      const data = result.data;
      setAreaNames(data);
    } catch (error) {
      console.error('Error fetching area names:', error);
    }
   // removeRequest();
  };

  useEffect(() => {
    fetchAreaNames();
  }, [language, product, client]);


  // fetchChildAreaNames fetches child area
  const fetchChildAreaNames = async (firstLevelArea) => {
    try {
      addRequest();

      const endpoint = ApiConstants.getChildAreasEndpoint(product, firstLevelArea, client);
      const url = `${ApiConstants.BASE_URL}${endpoint}`;
      const result = await authenticatedApiFetch(url);
      if (!result) return;
      const data = result.data;

      setChildAreaNames(data);
      const matchFound = Array.isArray(data) && data.some(area => area.productAreaId === secondLevelArea);
      setIsMatch(matchFound);
    } catch (error) {
      console.error('Error fetching child area names:', error);
    }
    removeRequest();
  };

  const [isMatch, setIsMatch] = useState(false);

  useEffect(() => {
    if (secondLevelArea) {
      fetchChildAreaNames(firstLevelArea);
    } else {
      setIsMatch(false);
    }
  }, [firstLevelArea, secondLevelArea]);

 
  //Composition
  const [questionsLoading, setQuestionsLoading] = useState(true);

  const fetchQuestions = async ({
    product,
    firstLevelArea,
    secondLevelArea,
    subArea,
    client,
    goToResults
  }) => {
    addRequest();
    setQuestionsLoading(true);

    const areaIdToUse = secondLevelArea || firstLevelArea;

    let hierarchicalQuestions = [];
    let newSubAreaName = '';
    let newPctGroup1 = null;
    let newPctGroup2 = null;
    let version = null;

    if (subArea) {
      const endpoint = ApiConstants.getCompositionDataEndpoint(product, areaIdToUse, subArea, client);

      const url = `${ApiConstants.BASE_URL}${endpoint}${goToResults ? '?goToResults=true' : ''}`;

      try {
        const response = await authenticatedApiFetch(url);

        if (response.status === 500) {
          setError("Failed to fetch composition.");
        }

        if (response.status === 200) {
          const data = response.data;

          if (data?.subAreaGroupLevel1DtoList) {
            hierarchicalQuestions = data.subAreaGroupLevel1DtoList;
          }

          newSubAreaName = data?.name || '';
          newPctGroup1 = data?.pctGroup1 ?? null;
          newPctGroup2 = data?.pctGroup2 ?? null;
          version = data?.version ?? null;

        } else {
          console.error("Composition API request failed:", response.status);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
        goToError();
        setError("Failed to fetch compositions. ", error);
      }
    }

    removeRequest();
    setQuestionsLoading(false);

    return { hierarchicalQuestions, newSubAreaName, newPctGroup1, newPctGroup2, version };
  };

  useEffect(() => {
    (async () => {
      setQuestionsLoading(true);

      const { hierarchicalQuestions, newSubAreaName, newPctGroup1, newPctGroup2, version } =
        await fetchQuestions({
          product,
          client,
          firstLevelArea,
          secondLevelArea,
          subArea,
          goToResults,
        });

      setQuestions(hierarchicalQuestions);
      setSubAreaName(newSubAreaName);
      setPctGroup1(newPctGroup1);
      setPctGroup2(newPctGroup2);
      setVersion(version);

      if (hierarchicalQuestions && hierarchicalQuestions.length > 0) {
        setWasValidated(false);
        formRef.current && formRef.current.classList.remove('was-validated');
      } else {
        setQuestions([]);
      }

      setQuestionsLoading(false);
    })();
  }, [firstLevelArea, secondLevelArea, subArea, paysafeFailed]);


  const {
    handleInputBlur,
    inputBlurTriggered,
    savingQuestionId,
    setSavingQuestionId,
    isSaving,
    saveQueueRef,
    validationErrorsRef,
  } = useInputBlurSave({
    product,
    client,
    subArea,
    firstLevelArea,
    secondLevelArea,
    goToResults,
    inputRefs,
    setQuestions,
    setSubAreaName,
    setPctGroup1,
    setPctGroup2,
    validationErrors,
    setValidationErrors,
    setIsDataSaved,
    setNavigateToIdMatrix,
    openModal,
    fetchLanguageForClient,
  });

  // === VALIDATION-ONLY FUNCTION (for Payment flow) ===
  const validateQuestionsViaNext = async () => {

    // Wait for all saves
    await saveQueueRef.current;

    // Check validation errors from saves
    if (validationErrorsRef.current && validationErrorsRef.current.length > 0) {
      const errorQuestionId =
        validationErrorsRef.current[0].compositionType +
        "_" +
        validationErrorsRef.current[0].id +
        "_" +
        validationErrorsRef.current[0].productId;
      scrollToError(errorQuestionId, inputRefs);
      return { hasErrors: true };
    }

    // Browser validation
    const form = formRef.current;
    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      form.classList.add("was-validated");
      setHasValidated(true);
      return { hasErrors: true };
    }

    setError(null);

    try {
      // Call NEXT API to validate
      const areaIdToUse = secondLevelArea || firstLevelArea;
      const endpoint = ApiConstants.getNextAreaDataEndpoint(
        product,
        areaIdToUse,
        subArea,
        client
      );
      const url = `${ApiConstants.BASE_URL}${endpoint}`;
      const result = await authenticatedApiFetch(url);

      if (!result) return { hasErrors: true };

      if (result.status === 500) {
        setError("Failed to validate page.");
        goToError();
        return { hasErrors: true };
      }

      const nextAreaData = result.data;
      const hasValidationErrors =
        nextAreaData?.errorDto?.validationErrors?.length > 0;

      if (hasValidationErrors) {
        const apiValidationErrors = nextAreaData.errorDto.validationErrors;

        const extractAllQuestions = (groupLevel1List = []) =>
          groupLevel1List.flatMap((group1) =>
            group1.subAreaGroupLevel2DtoList?.flatMap(
              (group2) => group2.subAreaCompositionDtoList || []
            ) || []
          );
        const allUpdatedQuestions = extractAllQuestions(
          nextAreaData.subAreaGroupLevel1DtoList || []
        );

        const cleanedErrors = validationErrorsRef.current.filter((prevError) => {
          const stillExists = allUpdatedQuestions.some(
            (q) =>
              q.id === prevError.id &&
              q.compositionType === prevError.compositionType
          );
          const shouldBeRemoved =
            !stillExists || prevError.resetOnChange === true;
          return !shouldBeRemoved;
        });

        const mergedErrors = [...cleanedErrors, ...apiValidationErrors];
        const dedupedErrors = mergedErrors.filter(
          (error, index, self) =>
            index ===
            self.findIndex(
              (e) =>
                e.id === error.id && e.compositionType === error.compositionType
            )
        );

        const formattedErrors = formatValidationErrors(dedupedErrors);
        setValidationErrors(formattedErrors);

        if (formattedErrors.length > 0) {
          const firstError = formattedErrors[0];
          const errorQuestionId = `${firstError.compositionType}_${firstError.id}_${firstError.productId}`;
          scrollToError(errorQuestionId, inputRefs);
        }

        return { hasErrors: true };
      }

      return {
        hasErrors: false,
        nextPageData: nextAreaData,
      };
    } catch (error) {
      setError("Failed to validate page.");
      console.error("Error during validation:", error);
      goToError();
      return { hasErrors: true };
    }
  };

  // === REGULAR NEXT (no payment) ===
  const handleNext = async () => {
    setNavigationIsLoadingNext(true);

    const validationResult = await validateQuestionsViaNext();

    if (validationResult.hasErrors) {
      setNavigationIsLoadingNext(false);
      return;
    }

    // Navigate to next page
    const nextAreaData = validationResult.nextPageData;

    if (nextAreaData.status === 200) {
      setPremiums([]);
    }

    setHasNext(nextAreaData.hasNext);
    setHasPrevious(nextAreaData.hasPrevious);

    if (nextAreaData.hasNext) {
      setHasValidated(false);
    }

    const { firstLevelAreaId, secondLevelAreaId, productSubAreaId } =
      nextAreaData;
    if (secondLevelAreaId) {
      setFirstLevelArea(firstLevelAreaId);
      setSecondLevelArea(secondLevelAreaId);
      setSubArea(productSubAreaId);
    } else {
      setFirstLevelArea(firstLevelAreaId);
      setSecondLevelArea(null);
      setSubArea(productSubAreaId);
    }

    scrollToTop();
    setNavigationIsLoadingNext(false);
  };

  // === PAY NOW (validate THEN pay THEN navigate) ===
 const handlePayNowClick = async () => {
  setNavigationIsLoadingNext(true);
  setIsPaymentProcessing(true);

  try {
    // STEP 1: Validate via NEXT
    const validationResult = await validateQuestionsViaNext();

    if (validationResult.hasErrors) {
      setIsPaymentProcessing(false);
      setNavigationIsLoadingNext(false);  // ADD THIS
      return false;
    }

    // STEP 2: Process payment
    if (paymentRef.current) {
      const paymentSuccess = isSimulation
        ? true
        : await paymentRef.current.handlePayNow();

      if (!paymentSuccess) {
        setIsPaymentProcessing(false);
        setNavigationIsLoadingNext(false);  // ADD THIS
        return false;
      }

      // STEP 3: Navigate using the data from validation
      const nextAreaData = validationResult.nextPageData;

      if (nextAreaData.status === 200) {
        setPremiums([]);
      }

      setHasNext(nextAreaData.hasNext);
      setHasPrevious(nextAreaData.hasPrevious);

      const { firstLevelAreaId, secondLevelAreaId, productSubAreaId } =
        nextAreaData;
      if (secondLevelAreaId) {
        setFirstLevelArea(firstLevelAreaId);
        setSecondLevelArea(secondLevelAreaId);
        setSubArea(productSubAreaId);
      } else {
        setFirstLevelArea(firstLevelAreaId);
        setSecondLevelArea(null);
        setSubArea(productSubAreaId);
      }

      scrollToTop();
      return true;
    }
  } catch (error) {
    console.error("PAY NOW error:", error);
    setError("Payment processing failed.");
    return false;
  } finally {
    setIsPaymentProcessing(false);
    setNavigationIsLoadingNext(false);  
  }
};








  //Previous
  const handlePrevious = async () => {

    const proceedWithPrevious = async () => {

      setError(null);

      // Reset the premiums

      setIsPaymentPage(false);

      setValidationErrors([]);

      setNavigationIsLoadingPrevious(true); // Start loading

      try {
        addRequest();

        const areaIdToUse = secondLevelArea || firstLevelArea;
        const endpoint = ApiConstants.getPrevAreaDataEndpoint(product, areaIdToUse, subArea, client);
        const url = `${ApiConstants.BASE_URL}${endpoint}`;
        const result = await authenticatedApiFetch(url);
        if (!result) return;

        if (result.status === 500) {
          setError("Failed to fetch previous page.");
        }

        if (result.status === 200) {
          // Reset the premiums
          setPremiums([]);
        }


        const prevAreaData = result.data;

        if (prevAreaData.errorDto && prevAreaData.errorDto.validationErrors.length > 0) {


          const errorQuestionId = prevAreaData.errorDto.validationErrors[0].compositionType + "_" + prevAreaData.errorDto.validationErrors[0].id + "_" + prevAreaData.errorDto.validationErrors[0].productId;


          scrollToError(errorQuestionId, inputRefs);

          setValidationErrors(prevAreaData.errorDto.validationErrors);  // Update state
          return;
        }

        setHasNext(prevAreaData.hasNext);
        setHasPrevious(prevAreaData.hasPrevious);

        const { firstLevelAreaId, secondLevelAreaId, productSubAreaId } = prevAreaData;
        setFirstLevelArea(firstLevelAreaId);
        setSecondLevelArea(secondLevelAreaId);
        setSubArea(productSubAreaId);

        scrollToTop();

      } catch (error) {
        console.error('Error fetching previous area data:', error);
        setError("Failed to fetch previous page. ", error);
      } finally {
        removeRequest();
        setNavigationIsLoadingPrevious(false);

      }

    };

    if (isSaving) {
      setTimeout(proceedWithPrevious, 300);  // Delay execution if saving
    } else {
      proceedWithPrevious();  // Proceed immediately if not saving
    }

  };

  //Breadcrumbs and Move Return
  const navigateToArea = async (productAreaId, productSubAreaId, clientId, destinationAreaId) => {
    setError(null);
    setValidationErrors([]);
    setIsPaymentPage(false);
    setPremiums([]);

    try {
      const endpoint = ApiConstants.getNavigateToAreaEndpoint(product, productAreaId, productSubAreaId, clientId, destinationAreaId);
      const url = `${ApiConstants.BASE_URL}${endpoint}`;
      const result = await authenticatedApiFetch(url);

      if (!result || result.status !== 200 || !result.data) {
        goToError();
        return;
      }

      const data = result.data;

      if (data.errorDto && data.errorDto.validationErrors && data.errorDto.validationErrors.length > 0) {
        const errorQuestionId =
          data.errorDto.validationErrors[0].compositionType +
          "_" +
          data.errorDto.validationErrors[0].id +
          "_" +
          data.errorDto.validationErrors[0].productId;

        scrollToError(errorQuestionId, inputRefs);
        setValidationErrors(data.errorDto.validationErrors);
        return;
      }

      if (Object.keys(data).length === 0) {
        goToError();
        return;
      }

      const { firstLevelAreaId, secondLevelAreaId, productSubAreaId: newProductSubAreaId } = data;

      setFirstLevelArea(firstLevelAreaId);
      setHasNext(data.hasNext);
      setHasPrevious(data.hasPrevious);

      if (secondLevelAreaId) {
        setSecondLevelArea(secondLevelAreaId);
      } else {
        setSecondLevelArea(null);
      }

      setSubArea(newProductSubAreaId);
      setGoToResults(false);


    } catch (error) {
      console.error('Error navigating to area:', error);
      setError("Failed to fetch navigation.", error);
      goToError();
    } finally {
    }
  };

  const handlePaymentPageLoad = () => {
    setIsPaymentPage(true);
  };

  // TOTAL PREMIUM FROM RESULTS LOGIC
  const [premiums, setPremiums] = useState([]); // State to track all premiums

  // Function to update the premiums array
  const handlePremiumChange = (newPremium, previousPremium, id) => {
    setPremiums((prevPremiums) => {
      // Remove the previous premium value if it exists
      const updatedPremiums = prevPremiums.filter(premium => premium !== previousPremium);
      // Add the new premium value
      const newPremiums = [...updatedPremiums, newPremium];
      return newPremiums;
    });
  };

  //PROGRESS BAR STATUS 2 LOGIC
  const getStatusForGroup2 = () => {
    if (pctGroup2 === null || pctGroup2 === undefined) {
      return t('not_necessary');
    }
    return pctGroup2 < 1 ? t('in_progress') : t('completed');
  };

  //for navigation with TAB to go to the top of the page
  const topRef = useRef(null);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.focus();
    }
  }, [firstLevelArea, secondLevelArea, subArea]); // Runs every time the page changes

  useEffect(() => {
    if (!product || !client) {
      console.error("Missing required data ... Redirecting to error page.");
      goToError();
    }
  }, [product, client]);


  const isFinalPage = hasNext === false && hasPrevious === false;

  const [isNavigationLoadingNext, setNavigationIsLoadingNext] = useState(false);
  const [isNavigationLoadingPrevious, setNavigationIsLoadingPrevious] = useState(false);


  //PAYMENT LOGIC

  const [isPayButtonDisabled, setIsPayButtonDisabled] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  const [isPaymentPage, setIsPaymentPage] = useState(false);

  const paymentRef = useRef(null);

  // PAYMENT DETECTION + FINAL PAGE RESET
  useEffect(() => {
    if (hasNext === false && hasPrevious === false) {
      setIsPaymentPage(false);
      return;
    }

    let hasPayment = false;

    questions.forEach(group => {
      group.subAreaGroupLevel2DtoList?.forEach(subGroup => {
        if (subGroup.uiRenderType === 'PAYMENT_PAYSAFE' || subGroup.uiRenderType === 'PAYMENT_EFT') {
          hasPayment = true;
        }
      });
    });

    setIsPaymentPage(hasPayment);
  }, [questions, hasNext, hasPrevious]);


  // --- VALIDITY CHECK ---
  useEffect(() => {
    if (isPaymentPage && paymentRef.current) {
      const checkValidity = () => {
        const hasValidationErrors =
          validationErrors && Object.keys(validationErrors).length > 0;
        const valid = isSimulation ? true : paymentRef.current?.isFormValid?.() ?? false;

        // Disable Pay button if not valid or validation errors exist
        setIsPayButtonDisabled(!valid || hasValidationErrors);
      };

      checkValidity();

      // Recheck every 500ms in case user changes EFT inputs
      const interval = setInterval(checkValidity, 500);
      return () => clearInterval(interval);
    } else {
      setIsPayButtonDisabled(true);
    }
  }, [isPaymentPage, paymentRef, validationErrors]);

  if (!product || !client) {
    goToError();
    return null; // Prevents rendering of the rest of the component
  }

  return (

    <>
      <SaleFlowDesign saleFlowId={product} />

      {/*  Top areas */}
      <div className="error text-center m-2">{error}</div>
      <nav>
        {areaNames.length > 0 && (
          <ul
            className={`list-group list-group-horizontal ${window.env?.REACT_APP_PAYSAFE_LIVE_MODE === "true"
              ? ""
              : "debug-mode"
              }`}
          >
            {areaNames.map((areaData, index) => (
              <li
                id={areaData.productAreaId}
                key={areaData.productAreaId}
                className={
                  areaData.productAreaId === firstLevelArea
                    ? "list-group-item current"
                    : "list-group-item"
                }
                onClick={
                  window.env?.REACT_APP_PAYSAFE_LIVE_MODE === "true"
                    ? undefined
                    : () => {
                      navigateToArea(firstLevelArea, subArea, client, areaData.productAreaId);
                    }
                }
              >
                {areaData.name}

              </li>
            ))}
          </ul>
        )}
      </nav>


      {error && <p>Error fetching data: {error.message}</p>}

      <>

        <div className={`content`}>

          {/* Second level sub-menu */}

          {isMatch &&

            <div className="sub-menu">
              {secondLevelArea && (
                <ul className="list-group list-group-horizontal">
                  {Array.isArray(childAreaNames) ? (
                    childAreaNames.map((childAreaData) => (
                      <li key={childAreaData.productAreaId}
                        className={childAreaData.productAreaId === secondLevelArea
                          ? "list-group-item current"
                          : "list-group-item"
                        }

                        onClick={() => {
                          navigateToArea(secondLevelArea, subArea, client, childAreaData.productAreaId);
                        }}
                      >
                        {childAreaData.name}
                      </li>
                    ))
                  ) : (
                    <li className="list-group-item">Error: Data could not be loaded.</li>
                  )}
                </ul>
              )}
            </div>

          }

          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            ref={formRef}
            id="product-page"
            noValidate
          >

            <div className={`form-q`}>

              <div className="form-title justify-content-between align-items-center">

                {subAreaName && <h1 ref={topRef} tabIndex="-1">{subAreaName}</h1>}

                {(pctGroup1) && (
                  <div className="application-progress">
                    <div>
                      <ProgressBar
                        percentage={pctGroup1}
                        progressName="Hyperjet"
                        progressStatus={pctGroup1 < 1 ? t('in_progress') : t('completed')}
                      />
                    </div>
                    <div>
                      <ProgressBar
                        percentage={pctGroup2}
                        progressName="Express"
                        progressStatus={getStatusForGroup2()}
                      />
                    </div>
                  </div>
                )}
              </div>

              {questions.map((group, index) => (
                <React.Fragment key={`group-${index}`}>

                  {group.name && (<div className="title" dangerouslySetInnerHTML={{ __html: group.name }} />)}

                  <div className={group.uiRenderType} data-group={group.productSubAreaId}>

                    {group.uiRenderType === 'AIS_SUMMARY' && (
                      <AISSummary
                        key={`ais-summary-${index}`}
                        client={client}
                        product={product}
                        setFirstLevelArea={setFirstLevelArea}
                        setSubArea={setSubArea}
                        setSecondLevelArea={setSecondLevelArea}
                        setHasNext={setHasNext}
                        setHasPrevious={setHasPrevious}
                        setPreviousFirstLevelArea={setPreviousFirstLevelArea}
                        setPreviousSecondLevelArea={setPreviousSecondLevelArea}
                        setPreviousSubArea={setPreviousSubArea}
                      />
                    )}

                    {group.uiRenderType === 'FAMILY' ? (
                      <FamilyMatrix
                        key={`family-${index}`}
                        group={group}
                        inputRefs={inputRefs}
                        handleInputBlur={handleInputBlur}
                        validationErrors={validationErrors}
                        navigateToId={navigateToIdMatrix || ""}
                      />
                    ) : (

                      group.subAreaGroupLevel2DtoList && group.subAreaGroupLevel2DtoList.map((subGroup, subIndex) => {

                        const renderSubGroupContent = () => {

                          // Handling for 'INSURABILITY_RESULT'          
                          if (subGroup.uiRenderType === 'INSURABILITY_RESULT') {
                            return (
                              <div key={`subGroup-${subIndex}`} >
                                {subGroup.name && (<div className="title" dangerouslySetInnerHTML={{ __html: subGroup.name }} />)}
                                <div className={subGroup.uiRenderType}>

                                  <div className="questions">

                                    {subGroup.subAreaCompositionDtoList.map((question) => {

                                      return (
                                        <div key={question.uniqueId} className={`entry ` + question.compositionType + ' ' + question.uniqueId + ' ' + question.code}
                                        >
                                          <Questions
                                            key={question.uniqueId}
                                            question={question}
                                            validationErrors={validationErrors}
                                            setValidationErrors={setValidationErrors}
                                            inputRefs={inputRefs}
                                            setQuestions={setQuestions}
                                            handleInputBlur={handleInputBlur}
                                            firstLevelArea={firstLevelArea}
                                            subArea={subArea}
                                            secondLevelArea={secondLevelArea}
                                            inputBlurTriggered={inputBlurTriggered}
                                            savingQuestionId={savingQuestionId}
                                            fetchAreaNames={fetchAreaNames}
                                            client={client}
                                            product={product}
                                            language={language}
                                            isDataSaved={isDataSaved}
                                            setIsDataSaved={setIsDataSaved}
                                            setGoToResults={setGoToResults}
                                          />
                                        </div>
                                      );
                                    })}

                                  </div>

                                </div>

                              </div>

                            );
                          }


                          // Handling for 'MAIN_PREMIUM'
                          else if (subGroup.uiRenderType === 'MAIN_PREMIUM') {
                            return (

                              <div key={`results-premium-${subIndex}`}
                              >
                                <span className='monthly-premium'>{t('monthly_premium')}:</span>
                                <MainPremium
                                  key={`main-premium-${subIndex}`}
                                  client={client}
                                  product={product}
                                  subIndex={subIndex}
                                  otherProductId={subGroup.otherProductId}
                                  onPremiumChange={(newPremium, previousPremium) => handlePremiumChange(newPremium, previousPremium, `main-premium-${subIndex}`)} // Pass the handler for premium changes with ID
                                  setDefaultProductPremium={setDefaultProductPremium}
                                />

                              </div>
                            );
                          }

                          // Handling for 'RESULTS_OFFER'
                          else if (subGroup.uiRenderType === 'RESULTS_OFFER') {

                            return (

                              <ResultsOffer
                                key={`results-offer-${subIndex}`}
                                client={client}
                                product={product}
                                subIndex={subIndex}
                                subGroup={subGroup}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                inputRefs={inputRefs}
                                setQuestions={setQuestions}
                                handleInputBlur={handleInputBlur}
                                firstLevelArea={firstLevelArea}
                                subArea={subArea}
                                secondLevelArea={secondLevelArea}
                                inputBlurTriggered={inputBlurTriggered}
                                savingQuestionId={savingQuestionId}
                                fetchAreaNames={fetchAreaNames}
                                language={language}
                                isDataSaved={isDataSaved}
                                setIsDataSaved={setIsDataSaved}
                                onPremiumChange={(newPremium, previousPremium) => handlePremiumChange(newPremium, previousPremium, `results-offer-${subIndex}`)} // Pass the handler for premium changes with ID
                                otherProductId={subGroup.otherProductId}
                              />

                            );
                          }

                          else if (subGroup.uiRenderType === 'RESULTS_TOTAL_PREMIUM') {

                            return (
                              <TotalPremium
                                key={`total-premium-${subIndex}`}
                                premiums={premiums}
                                initialProductValue={defaultProductPremium} // Pass the premiums array to TotalPremium
                                product={product}
                                productId={subGroup.productId}
                                client={client}


                              />
                            );
                          }


                          // Handling for 'ADDRESS'
                          else if (subGroup.uiRenderType === 'ADDRESS') {
                            return (

                              <Address
                                key={`address-${subIndex}`}
                                subGroup={subGroup}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                inputRefs={inputRefs}
                                setQuestions={setQuestions}
                                handleInputBlur={handleInputBlur}
                                firstLevelArea={firstLevelArea}
                                subArea={subArea}
                                secondLevelArea={secondLevelArea}
                                inputBlurTriggered={inputBlurTriggered}
                                savingQuestionId={savingQuestionId}
                                fetchAreaNames={fetchAreaNames}
                                client={client}
                                product={product}
                                language={language}
                                setIsDataSaved={setIsDataSaved}
                              />

                            );
                          }

                          // Handling for 'HEIGHT'
                          else if (subGroup.uiRenderType === 'HEIGHT_SUBGROUP') {

                            return (

                              <Height
                                key={`height-${subIndex}`}
                                subGroup={subGroup}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                inputRefs={inputRefs}
                                setQuestions={setQuestions}
                                handleInputBlur={handleInputBlur}
                                firstLevelArea={firstLevelArea}
                                subArea={subArea}
                                secondLevelArea={secondLevelArea}
                                inputBlurTriggered={inputBlurTriggered}
                                savingQuestionId={savingQuestionId}
                                fetchAreaNames={fetchAreaNames}
                                client={client}
                                product={product}
                                language={language}
                                setIsDataSaved={setIsDataSaved}
                                setSavingQuestionId={setSavingQuestionId}
                              />
                            );
                          }


                          // Handling for 'HEIGHT'
                          else if (subGroup.uiRenderType === 'WEIGHT_SUBGROUP') {

                            return (

                              <Weight
                                key={`weight-${subIndex}`}
                                subGroup={subGroup}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                inputRefs={inputRefs}
                                setQuestions={setQuestions}
                                handleInputBlur={handleInputBlur}
                                firstLevelArea={firstLevelArea}
                                subArea={subArea}
                                secondLevelArea={secondLevelArea}
                                inputBlurTriggered={inputBlurTriggered}
                                savingQuestionId={savingQuestionId}
                                fetchAreaNames={fetchAreaNames}
                                client={client}
                                product={product}
                                language={language}
                                setIsDataSaved={setIsDataSaved}
                                setSavingQuestionId={setSavingQuestionId}
                              />
                            );
                          }

                          else if (subGroup.uiRenderType === 'POLICY_INFO') {
                            return (

                              <PolicyInfo
                                key={`policy-info-${subGroup.productSubAreaId}`}
                                client={client}
                                product={product}
                                productId={subGroup.productId}
                                subGroup={subGroup}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                inputRefs={inputRefs}
                                setQuestions={setQuestions}
                                handleInputBlur={handleInputBlur}
                                firstLevelArea={firstLevelArea}
                                subArea={subArea}
                                secondLevelArea={secondLevelArea}
                                inputBlurTriggered={inputBlurTriggered}
                                savingQuestionId={savingQuestionId}
                                fetchAreaNames={fetchAreaNames}
                                language={language}
                                setIsDataSaved={setIsDataSaved}
                              />

                            );
                          }

                          else if (subGroup.uiRenderType === 'ADVISOR_IDENTIFICATION') {
                            return (
                              <AdvisorsIdentification
                                key={`advisor-identification-${subGroup.productSubAreaId}`}
                                client={client}
                                product={product}
                                subGroup={subGroup}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                inputRefs={inputRefs}
                                setQuestions={setQuestions}
                                handleInputBlur={handleInputBlur}
                                firstLevelArea={firstLevelArea}
                                subArea={subArea}
                                secondLevelArea={secondLevelArea}
                                inputBlurTriggered={inputBlurTriggered}
                                savingQuestionId={savingQuestionId}
                                fetchAreaNames={fetchAreaNames}
                                language={language}
                                setIsDataSaved={setIsDataSaved}
                              />
                            );
                          }

                          else if (subGroup.uiRenderType === 'SECOND_ADVISOR') {
                            return (
                              <SecondAdvisorsIdentification
                                key={`second-advisor-identification-${subGroup.productSubAreaId}`}
                                client={client}
                                product={product}
                                subGroup={subGroup}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                inputRefs={inputRefs}
                                setQuestions={setQuestions}
                                handleInputBlur={handleInputBlur}
                                firstLevelArea={firstLevelArea}
                                subArea={subArea}
                                secondLevelArea={secondLevelArea}
                                inputBlurTriggered={inputBlurTriggered}
                                savingQuestionId={savingQuestionId}
                                fetchAreaNames={fetchAreaNames}
                                language={language}
                                setIsDataSaved={setIsDataSaved}
                              />
                            );
                          }




                          else if (subGroup.uiRenderType === 'PAYMENT_PAYSAFE') {
                            return (
                              <PaymentPaySafe
                                ref={paymentRef}
                                key={`payment-paysafe-${subGroup.productSubAreaId}`}
                                client={client}
                                productId={subGroup.productId}
                                product={product}
                                onPaymentPageLoad={handlePaymentPageLoad}
                                validationErrors={validationErrors}
                                setPaysafeFailed={setPaysafeFailed}
                              />
                            );
                          }

                          else if (subGroup.uiRenderType === 'PAYMENT_EFT') {
                            return (
                              <PaymentEFT
                                ref={paymentRef}
                                key={`payment-eft-${subGroup.productSubAreaId}`}
                                product={product}
                                productId={subGroup.productId}
                                client={client}
                                setFirstLevelArea={setFirstLevelArea}
                                setSubArea={setSubArea}
                                setSecondLevelArea={setSecondLevelArea}
                                setHasNext={setHasNext}
                                setHasPrevious={setHasPrevious}
                                onPaymentPageLoad={handlePaymentPageLoad}
                                handlePrevious={handlePrevious}
                                handleNext={handleNext}
                              />
                            );
                          }







                          else if (subGroup.subAreaCompositionDtoList) {
                            return (
                              <div key={`subGroup-${subIndex}`}>
                                {subGroup.name && (
                                  <div
                                    className={`title${subGroup.uiRenderType ? `-${subGroup.uiRenderType}` : ''}`}
                                    dangerouslySetInnerHTML={{ __html: subGroup.name }}
                                  />
                                )}
                                <div className={subGroup.uiRenderType}>
                                  <div className="questions" style={{ display: "flex", flexWrap: "wrap" }}>
                                    <AnimatePresence mode="popLayout">
                                      {subGroup.subAreaCompositionDtoList.map((question) => {
                                        const sameRowQuestionsCount = question.sameRow
                                          ? subGroup.subAreaCompositionDtoList.filter(
                                            (q) => q.sameRow === question.sameRow
                                          ).length
                                          : 1;
                                        const questionWidth = question.sameRow
                                          ? `${100 / sameRowQuestionsCount}%`
                                          : "100%";

                                        return (
                                          <motion.div
                                            key={`question-${question.uniqueId}`}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                            className={
                                              `${question.sameRow ? "entry same-row" : "entry"} ` +
                                              `${question.compositionType} ${question.uniqueId} ` +
                                              `${question.uiRenderType === "BEN_ADD_REM" ? "BEN_ADD_REM" : ""} ` +
                                              `${question.uiRenderType === "BEN_ADD_REM_FIRST" && question.value === 2 ? "BEN_ADD_REM_FIRST_HIDE" : ""} ` +
                                              `${question.uiRenderType === "BEN_ADD_REM_FIRST" && question.value === 1 ? "BEN_ADD_REM" : ""} ` +
                                              `${question.uiRenderType === "BEN_ADD_REM_FIRST" && question.value === 0 ? "BEN_ADD_REM" : ""}`
                                            }
                                            style={{
                                              width: questionWidth,
                                              display: "inline-block",
                                            }}
                                          >
                                            <Questions
                                              key={question.uniqueId}
                                              question={question}
                                              validationErrors={validationErrors}
                                              setValidationErrors={setValidationErrors}
                                              inputRefs={inputRefs}
                                              setQuestions={setQuestions}
                                              handleInputBlur={handleInputBlur}
                                              firstLevelArea={firstLevelArea}
                                              subArea={subArea}
                                              secondLevelArea={secondLevelArea}
                                              inputBlurTriggered={inputBlurTriggered}
                                              savingQuestionId={savingQuestionId}
                                              fetchAreaNames={fetchAreaNames}
                                              client={client}
                                              product={product}
                                              language={language}
                                              isDataSaved={isDataSaved}
                                              setIsDataSaved={setIsDataSaved}
                                              setFirstLevelArea={setFirstLevelArea}
                                              setSubArea={setSubArea}
                                              setSecondLevelArea={setSecondLevelArea}
                                              setHasNext={setHasNext}
                                              setHasPrevious={setHasPrevious}
                                              setPreviousFirstLevelArea={setPreviousFirstLevelArea}
                                              setPreviousSecondLevelArea={setPreviousSecondLevelArea}
                                              setPreviousSubArea={setPreviousSubArea}
                                              setGoToResults={setGoToResults}
                                            />
                                          </motion.div>
                                        );
                                      })}
                                    </AnimatePresence>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          // Return something for test... empty
                          return <div key={subIndex}></div>;
                        };

                        // Render the subgroup content
                        return renderSubGroupContent();
                      })
                    )}

                  </div>
                </React.Fragment>
              ))
              }

              <Modal
                isVisible={isModalVisible}
                title={modalContent.title}
                content={modalContent.content}
                onClose={closeModal}
              />









              <div className="buttons text-center">
                {hasPrevious && !goToResults && (
                  <button
                    type="button"
                    className="btn btn-primary btn-light left"
                    onClick={handlePrevious}
                  >
                    {t('previous')}
                    {isNavigationLoadingPrevious && (
                      <div className="loading-previous"><div className="loading"></div></div>
                    )}
                  </button>
                )}

                {!isPaymentPage ? (
                  goToResults ? (
                    <button
                      type="button"
                      className="btn btn-primary right"
                      onClick={() => {
                        navigateToArea(firstLevelArea, subArea, client, previousFirstLevelArea);
                      }}
                      disabled={validationErrors?.length > 0}
                    >
                      {t('go_back')}
                    </button>
                  ) : (
                    <>
                      {hasNext && (
                        <button
                          type="button"
                          className="btn btn-primary right"
                          onClick={handleNext}
                          disabled={validationErrors.current?.length > 0}
                        >
                          {t('next')}
                          {isNavigationLoadingNext && (
                            <div className="loading-next"><div className="loading"></div></div>
                          )}
                        </button>
                      )}

                      {isFinalPage && (
                        <button
                          type="button"
                          className="btn-purple right"
                          onClick={goToApplications}
                        >
                          {t('return_to_applications')}
                        </button>
                      )}
                    </>
                  )
                ) : (
                  <button
      type="button"
      className="btn btn-primary right"
      onClick={handlePayNowClick}
      disabled={isPayButtonDisabled || isPaymentProcessing}
    >
      {t('pay_now')}
      {isNavigationLoadingNext && (
        <div className="loading-next"><div className="loading"></div></div>
      )}
    </button>
                )}
              </div>





            </div>
          </form>
          {isDebugMode() && (
            <div>
              <div className="dev-areas">
                firstLevelArea: {firstLevelArea} &nbsp;
                secondLevelArea: {secondLevelArea} &nbsp;
                subArea: {subArea}
              </div>
              <div className="app-version" style={{ textAlign: 'center' }}> version: {version}</div>
            </div>

          )}

        </div>
      </>
    </>
  );
};

export default Product;